/**
 * MyStatsScreen — reportes personales del coleccionista.
 *
 * Muestra al user su propio progreso de manera amigable: % del álbum,
 * equipo más lleno, progreso por equipo, top repetidas y resumen de tratos.
 *
 * Toda la data viene de endpoints existentes — no agrega lógica nueva en el
 * frontend, solo presenta lo que ya está disponible.
 */
import { useEffect, useMemo, useState } from "react";
import { Icon } from "../lib/Icon";
import { useAuth } from "../contexts/AuthContext";
import { useCatalog } from "../contexts/CatalogContext";
import { useInventory } from "../contexts/InventoryContext";
import { fetchMyStats, fetchMyTeamStats, type AlbumStats, type TeamProgress } from "../api/inventory";
import { fetchProposalSummary, type TradeSummary } from "../api/trades";
import type { Route } from "../App";

interface Props {
  go: (r: Route) => void;
}

export function MyStatsScreen({ go }: Props) {
  const { user } = useAuth();
  const { stickerById, teamById } = useCatalog();
  const { ownership } = useInventory();
  const [stats, setStats] = useState<AlbumStats | null>(null);
  const [teamStats, setTeamStats] = useState<TeamProgress[]>([]);
  const [tradeSummary, setTradeSummary] = useState<TradeSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchMyStats(),
      fetchMyTeamStats(),
      fetchProposalSummary().catch(() => null),
    ])
      .then(([s, t, ts]) => {
        setStats(s);
        setTeamStats(t);
        setTradeSummary(ts);
      })
      .finally(() => setLoading(false));
  }, [ownership]);

  // Top 10 láminas que más tengo en duplicado (count >= 2)
  const topDups = useMemo(() => {
    return Object.entries(ownership)
      .map(([sid, c]) => ({ stickerId: Number(sid), count: c }))
      .filter((x) => x.count >= 2)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [ownership]);

  if (loading || !stats) {
    return <div style={{ padding: 40, textAlign: "center", color: "var(--fg-muted)" }}>Cargando estadísticas…</div>;
  }
  if (!user) return null;

  const top = teamStats[0];
  const allFull = teamStats.filter((t) => t.pct === 100);
  const inProgress = teamStats.filter((t) => t.pct > 0 && t.pct < 100);
  const notStarted = teamStats.filter((t) => t.pct === 0);

  return (
    <div className="page" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Header */}
      <div>
        <div className="chip" style={{ marginBottom: 6 }}>
          <Icon name="sparkle" size={11} /> Mis estadísticas
        </div>
        <h1 style={{ fontFamily: "var(--font-display)", fontSize: 32, fontWeight: 800, margin: 0, letterSpacing: "-0.02em" }}>
          Tu progreso, {user.name.split(" ")[0]} 🎴
        </h1>
        <p style={{ color: "var(--fg-muted)", margin: "4px 0 0" }}>
          Cómo va tu álbum, en qué equipos vas más fuerte y qué láminas te sobran para intercambiar.
        </p>
      </div>

      {/* Hero — % global del álbum */}
      <section className="card stats-hero" style={{ padding: 20, background: "linear-gradient(135deg, var(--primary-soft) 0%, var(--bg-card) 70%)" }}>
        <div className="stats-hero-grid">
          <div className="stats-hero-circle">
            <CircularProgress pct={stats.pct} size={180} stroke={12} />
          </div>
          <div className="stats-hero-text">
            <div className="stat-label">Álbum completado</div>
            <div className="stats-hero-pct">{stats.pct}%</div>
            <div className="stats-hero-stats">
              <Stat label="Pegadas" value={stats.owned} accent="var(--primary)" />
              <Stat
                label="Repetidas"
                value={stats.dups_unique === stats.dups
                  ? stats.dups
                  : `${stats.dups_unique}/${stats.dups}`}
                accent="var(--accent-amber)"
                hint={stats.dups_unique === stats.dups
                  ? undefined
                  : `${stats.dups_unique} distintas · ${stats.dups} copias totales`}
              />
              <Stat label="Faltantes" value={stats.missing} accent="var(--accent-magenta)" />
              <Stat label="Total" value={stats.total} accent="var(--fg-muted)" />
            </div>
            <div className="stats-hero-actions">
              <button className="btn btn-primary btn-sm" onClick={() => go("album-book")}>
                <Icon name="book" size={12} /> Ir a mi álbum
              </button>
              {stats.dups > 0 && (
                <button className="btn btn-secondary btn-sm" onClick={() => go("matches")}>
                  <Icon name="swap" size={12} /> Buscar matches
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Resumen de tratos */}
      <section>
        <h3 style={{ marginBottom: 8, fontSize: 16 }}>Tus intercambios</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
          <div className="card" style={{ borderTop: "3px solid var(--primary)" }}>
            <div className="stat-label">Nivel coleccionista</div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>Nv. {user.level}</div>
            <div className="tiny muted" style={{ marginTop: 4 }}>{user.xp} XP totales</div>
          </div>
          <div className="card" style={{ borderTop: "3px solid var(--primary)" }}>
            <div className="stat-label">Trueques completados</div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>{user.trades_completed}</div>
          </div>
          {tradeSummary && (
            <>
              <div className="card" style={{ borderTop: "3px solid var(--accent-amber)" }}>
                <div className="stat-label">Por aceptar</div>
                <div style={{ fontSize: 28, fontWeight: 700 }}>{tradeSummary.incoming_pending}</div>
                <div className="tiny muted" style={{ marginTop: 4 }}>te enviaron</div>
              </div>
              <div className="card" style={{ borderTop: "3px solid var(--accent-cobalt)" }}>
                <div className="stat-label">Esperando respuesta</div>
                <div style={{ fontSize: 28, fontWeight: 700 }}>{tradeSummary.outgoing_pending}</div>
                <div className="tiny muted" style={{ marginTop: 4 }}>enviaste</div>
              </div>
            </>
          )}
          <div className="card" style={{ borderTop: "3px solid var(--accent-magenta)" }}>
            <div className="stat-label">Estrellas</div>
            <div style={{ fontSize: 28, fontWeight: 700 }}>
              <Icon name="star" size={20} /> {user.stars_avg.toFixed(1)}
            </div>
          </div>
        </div>
      </section>

      {/* Equipo más lleno destacado */}
      {top && top.pct > 0 && (
        <section>
          <h3 style={{ marginBottom: 8, fontSize: 16 }}>🏆 Equipo más lleno</h3>
          <div className="card" style={{ display: "flex", alignItems: "center", gap: 16, padding: 16, background: `linear-gradient(135deg, ${top.team_color}26 0%, var(--bg-card) 70%)` }}>
            <div style={{ width: 56, height: 56, borderRadius: 12, background: top.team_color, display: "grid", placeItems: "center", color: "white", fontWeight: 800, fontSize: 18, flexShrink: 0 }}>
              {top.team_code.slice(0, 3)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 18 }}>{top.team_name}</div>
              <div className="tiny muted" style={{ marginTop: 2 }}>
                {top.owned} de {top.total} láminas pegadas{top.dups > 0 ? ` · +${top.dups} repetidas` : ""}
              </div>
              <ProgressBar pct={top.pct} color={top.team_color} />
            </div>
            <div style={{ fontSize: 32, fontWeight: 800, color: top.team_color, minWidth: 80, textAlign: "right" }}>
              {top.pct}%
            </div>
          </div>
        </section>
      )}

      {/* Progreso por equipo */}
      {teamStats.length > 0 && (
        <section>
          <h3 style={{ marginBottom: 8, fontSize: 16 }}>Progreso por equipo</h3>
          <div className="tiny muted" style={{ marginBottom: 12 }}>
            {allFull.length} completos · {inProgress.length} en progreso · {notStarted.length} sin empezar
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
            {teamStats.map((t) => (
              <div key={t.team_id} className="card" style={{ padding: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 6, background: t.team_color, color: "white", display: "grid", placeItems: "center", fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
                    {t.team_code.slice(0, 3)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.team_name}</div>
                    <div className="tiny muted">{t.owned}/{t.total}{t.dups > 0 ? ` · +${t.dups} repes` : ""}</div>
                  </div>
                  <div style={{ fontWeight: 700, color: t.pct === 100 ? "var(--primary)" : "var(--fg)" }}>
                    {t.pct === 100 ? "✓ 100%" : `${t.pct}%`}
                  </div>
                </div>
                <ProgressBar pct={t.pct} color={t.team_color} thin />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Mis top repetidas */}
      {topDups.length > 0 && (
        <section>
          <h3 style={{ marginBottom: 8, fontSize: 16 }}>🔥 Tus láminas más repetidas</h3>
          <div className="tiny muted" style={{ marginBottom: 12 }}>
            Las que tenés en mayor cantidad — ideales para intercambiar. Andá a Matches para ver con quién hacer trade.
          </div>
          <div className="card" style={{ padding: 0, overflowX: "auto" }}>
            <table style={{ minWidth: 480, width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "var(--bg-subtle)", textAlign: "left" }}>
                  <th style={cellHead}>#</th>
                  <th style={cellHead}>Lámina</th>
                  <th style={cellHead}>Equipo</th>
                  <th style={cellHead}>Cuántas</th>
                </tr>
              </thead>
              <tbody>
                {topDups.map((row, i) => {
                  const sticker = stickerById(row.stickerId);
                  const team = sticker ? teamById(sticker.team_id) : null;
                  return (
                    <tr key={row.stickerId} style={{ borderTop: "1px solid var(--border)" }}>
                      <td style={cell}>{i + 1}</td>
                      <td style={cell}>
                        <b>{sticker?.num || "?"}</b> · {sticker?.name || "(eliminada)"}
                      </td>
                      <td style={cell} className="tiny muted">{team?.name || "—"}</td>
                      <td style={cell}>
                        <span className="chip amber">×{row.count}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

// ============================================================================

function Stat({ label, value, accent, hint }: { label: string; value: number | string; accent: string; hint?: string }) {
  return (
    <div style={{ minWidth: 80 }} title={hint}>
      <div className="tiny muted">{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: accent, lineHeight: 1.2 }}>
        {typeof value === "number" ? value.toLocaleString("es-CO") : value}
      </div>
      {hint && <div className="tiny muted" style={{ fontSize: 10, marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

function ProgressBar({ pct, color, thin = false }: { pct: number; color: string; thin?: boolean }) {
  return (
    <div style={{ marginTop: 6, height: thin ? 6 : 8, background: "var(--bg-subtle)", borderRadius: 4, overflow: "hidden" }}>
      <div style={{ width: `${pct}%`, height: "100%", background: color, transition: "width 0.4s ease" }} />
    </div>
  );
}

function CircularProgress({ pct, size = 180, stroke = 12 }: { pct: number; size?: number; stroke?: number }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;
  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${size} ${size}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ display: "block", margin: "0 auto", maxWidth: size }}
    >
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--bg-subtle)" strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={radius}
        fill="none" stroke="var(--primary)" strokeWidth={stroke}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: "stroke-dashoffset 0.6s ease" }}
      />
      <text
        x="50%" y="50%" textAnchor="middle" dominantBaseline="central"
        style={{ fontSize: size * 0.22, fontWeight: 800, fontFamily: "var(--font-display)", fill: "var(--fg)" }}
      >
        {pct}%
      </text>
    </svg>
  );
}

const cellHead: React.CSSProperties = {
  padding: "10px 12px",
  fontWeight: 600,
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "var(--fg-muted)",
};

const cell: React.CSSProperties = { padding: "10px 12px", verticalAlign: "middle" };
